import { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Clock,
  AlertTriangle,
  CheckCircle2,
  ChefHat,
  Flame,
  User,
  StickyNote,
  ArrowRight,
} from 'lucide-react';
import { KDSOrder, OrderPriority, OrderItemStatus } from '../../types/kds';

interface KDSOrderCardProps {
  order: KDSOrder;
  onBump: (orderId: string) => void;
  onUpdatePriority: (orderId: string, priority: OrderPriority) => void;
  onItemToggle?: (itemId: string) => void;
}

export function KDSOrderCard({
  order,
  onBump,
  onItemToggle,
}: KDSOrderCardProps) {

  // Calculate time color based on elapsed vs estimated
  const timeColor = useMemo(() => {
    if (!order.estimatedPreparationTime) return 'text-gray-400';

    const progress = (order.elapsedMinutes / order.estimatedPreparationTime) * 100;

    if (progress < 50) return 'text-green-500';
    if (progress < 80) return 'text-yellow-500';
    if (progress < 100) return 'text-orange-500';
    return 'text-red-500 animate-pulse';
  }, [order.elapsedMinutes, order.estimatedPreparationTime]);

  // Priority badge styling
  const priorityConfig = useMemo(() => {
    switch (order.priority) {
      case OrderPriority.URGENT:
        return {
          bg: 'bg-red-500/20',
          text: 'text-red-500',
          border: 'border-red-500',
          icon: <Flame className="w-4 h-4" />,
          label: 'URGENTE',
        };
      case OrderPriority.HIGH:
        return {
          bg: 'bg-orange-500/20',
          text: 'text-orange-500',
          border: 'border-orange-500',
          icon: <AlertTriangle className="w-4 h-4" />,
          label: 'ALTA',
        };
      case OrderPriority.DELAYED:
        return {
          bg: 'bg-red-600/30',
          text: 'text-red-600',
          border: 'border-red-600',
          icon: <Clock className="w-4 h-4 animate-bounce" />,
          label: 'IN RITARDO',
        };
      default:
        return null;
    }
  }, [order.priority]);

  // Status badge
  const statusBadge = useMemo(() => {
    switch (order.status) {
      case 'SUBMITTED':
        return { text: 'NUOVO', className: 'bg-blue-500 animate-pulse' };
      case 'PREPARING':
        return { text: 'IN PREPARAZIONE', className: 'bg-yellow-500' };
      case 'READY':
        return { text: 'PRONTO', className: 'bg-green-500 animate-pulse' };
      default:
        return { text: order.status, className: 'bg-gray-500' };
    }
  }, [order.status]);

  // Count items by status
  const itemStats = useMemo(() => {
    const total = order.items.length;
    const ready = order.items.filter((i) => i.itemStatus === OrderItemStatus.READY).length;
    const preparing = order.items.filter(
      (i) => i.itemStatus === OrderItemStatus.PREPARING
    ).length;

    return { total, ready, preparing, pending: total - ready - preparing };
  }, [order.items]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9, x: 100 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className={`relative overflow-hidden rounded-2xl
        ${order.isDelayed
          ? 'bg-red-50 dark:bg-red-950/40 border-2 border-red-500 shadow-red-500/20 dark:shadow-red-500/50'
          : 'bg-white dark:bg-[#25252d] border border-gray-200 dark:border-white/[0.08]'}
        shadow-sm hover:shadow-lg dark:shadow-none transition-all duration-300 group`}
    >
      {/* Glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      {/* Header */}
      <div className="relative p-4 border-b border-gray-100 dark:border-white/[0.06]">
        <div className="flex items-start justify-between mb-2">
          {/* Table number */}
          <div className="flex items-center gap-3">
            <div className="text-2xl font-black text-white bg-gradient-to-br from-orange-500 to-red-500 rounded-xl px-4 py-2 shadow-lg shadow-orange-500/25">
              {order.tableNumber}
            </div>
            {order.customerName && (
              <div className="flex items-center gap-2 text-gray-600 dark:text-white/70">
                <User className="w-4 h-4" />
                <span className="font-medium">{order.customerName}</span>
              </div>
            )}
          </div>

          {/* Status badge */}
          <div className={`${statusBadge.className} text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg`}>
            {statusBadge.text}
          </div>
        </div>

        {/* Timer and Priority */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-3">
            {/* Timer */}
            <div className={`flex items-center gap-2 ${timeColor} font-mono text-base font-bold`}>
              <Clock className={`w-4 h-4 ${order.isDelayed ? 'animate-pulse' : ''}`} />
              <span>{order.elapsedMinutes}m</span>
              {order.estimatedPreparationTime && (
                <span className="text-gray-400 dark:text-white/40 text-sm">
                  / {order.estimatedPreparationTime}m
                </span>
              )}
            </div>

            {/* Priority badge */}
            {priorityConfig && (
              <div
                className={`flex items-center gap-1.5 ${priorityConfig.bg} ${priorityConfig.text}
                  border ${priorityConfig.border} rounded-lg px-2.5 py-1 font-bold text-xs`}
              >
                {priorityConfig.icon}
                {priorityConfig.label}
              </div>
            )}
          </div>

          {/* Item progress */}
          <div className="flex items-center gap-1.5 text-sm">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
            <span className="font-semibold text-gray-900 dark:text-white">
              {itemStats.ready}/{itemStats.total}
            </span>
            <span className="text-gray-400 dark:text-white/40">items</span>
          </div>
        </div>
      </div>

      {/* Items list */}
      <div className="p-4 space-y-2 max-h-80 overflow-y-auto custom-scrollbar">
        {order.items.map((item, index) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            onClick={() => onItemToggle?.(item.id)}
            className={`flex items-center justify-between p-3 rounded-xl cursor-pointer
              transition-all duration-200 hover:scale-[1.01]
              ${
                item.itemStatus === OrderItemStatus.READY
                  ? 'bg-emerald-50 dark:bg-emerald-500/20 border border-emerald-500'
                  : item.itemStatus === OrderItemStatus.PREPARING
                  ? 'bg-yellow-50 dark:bg-yellow-500/20 border border-yellow-500'
                  : 'bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] hover:bg-gray-100 dark:hover:bg-white/[0.06]'
              }`}
          >
            <div className="flex items-center gap-3 flex-1">
              <div className="text-xl font-bold text-gray-900 dark:text-white">{item.quantity}x</div>
              <div>
                <div className="font-semibold text-gray-900 dark:text-white">{item.productName}</div>
                {item.customizations && typeof item.customizations === 'object' && (
                  <div className="text-xs text-gray-500 dark:text-white/50 mt-1 space-y-0.5">
                    {item.customizations.selectedOptions?.map((option: any, idx: number) => (
                      <div key={idx}>
                        <span className="font-medium">{option.optionGroupName}:</span> {option.optionName}
                        {option.priceModifier !== 0 && (
                          <span className={option.priceModifier > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                            {' '}({option.priceModifier > 0 ? '+' : ''}€{option.priceModifier.toFixed(2)})
                          </span>
                        )}
                      </div>
                    ))}
                    {item.customizations.freeTextNotes && (
                      <div className="italic">{item.customizations.freeTextNotes}</div>
                    )}
                  </div>
                )}
                {item.customizations && typeof item.customizations === 'string' && (
                  <div className="text-xs text-gray-500 dark:text-white/50 mt-1">{item.customizations}</div>
                )}
                {item.kitchenStationName && (
                  <div
                    className="inline-flex items-center gap-1 mt-1 text-xs font-medium px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: `${item.kitchenStationColor}20`,
                      color: item.kitchenStationColor,
                    }}
                  >
                    <ChefHat className="w-3 h-3" />
                    {item.kitchenStationName}
                  </div>
                )}
              </div>
            </div>

            {/* Item status icon */}
            <div>
              {item.itemStatus === OrderItemStatus.READY ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-500 dark:text-emerald-400" />
              ) : item.itemStatus === OrderItemStatus.PREPARING ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                >
                  <ChefHat className="w-5 h-5 text-yellow-500 dark:text-yellow-400" />
                </motion.div>
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-white/30" />
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Notes */}
      {order.notes && (
        <div className="px-4 pb-4">
          <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl p-3">
            <StickyNote className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-100">{order.notes}</p>
          </div>
        </div>
      )}

      {/* Bump button */}
      <div className="p-4 pt-0">
        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={() => {
            console.log('🖱️ Button clicked for order:', order.id);
            onBump(order.id);
          }}
          className={`w-full py-3.5 rounded-xl font-bold text-base shadow-lg
            transition-all duration-300 flex items-center justify-center gap-2
            ${
              order.status === 'READY'
                ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-500/25'
                : order.status === 'PREPARING'
                ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-blue-500/25'
                : 'bg-orange-500 hover:bg-orange-600 text-white shadow-orange-500/25'
            }`}
        >
          {order.status === 'READY' ? (
            <>
              <CheckCircle2 className="w-5 h-5" />
              SERVITO
            </>
          ) : order.status === 'PREPARING' ? (
            <>
              <CheckCircle2 className="w-5 h-5" />
              PRONTO
            </>
          ) : (
            <>
              <ChefHat className="w-5 h-5" />
              INIZIA
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </motion.button>
      </div>
    </motion.div>
  );
}
